import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { useAtom } from "jotai";
import { Document, Page, View, Image, PDFViewer, Text } from "@react-pdf/renderer";
import { displayMunicNameAtom, imageResponseAtom, jobDensityMapDataAtom, jobDensityMapDataGreyAtom, jobDensityMaxValueAtom, jobDensityMinValueAtom, jobDensityReportKeyAtom, selectedFiltersAtom, selectedPlaceAtom, selectedSpatialLayerAtom } from "../../shared/state/atoms";
import { Box, CircularProgress } from "@mui/material";
import styles from "../../pdfs/styles/PdfStyles";
import { DashboardMap } from "../../components/Dashboards/components";

/**
 * Job Density PDF Component
 *
 * This component renders the Job Density dashboard as a PDF.
 * It includes a Mapbox GL map showing job density.
 *
 * IMPORTANT NOTES ON PDF GENERATION:
 * - The component uses map loading detection to ensure the map is fully rendered
 * - It listens for Mapbox GL 'load' and 'idle' events before capturing
 * - Multiple timeouts and fallbacks ensure the PDF generation completes
 * - See /docs/PDF_GENERATION.md for more details on the PDF generation process
 */
function JobDensityPdf() {
  const [maxValue] = useAtom(jobDensityMaxValueAtom);
  const [minValue] = useAtom(jobDensityMinValueAtom);
  const [mapData] = useAtom(jobDensityMapDataAtom);
  const [mapDataGrey] = useAtom(jobDensityMapDataGreyAtom);
  const [reportKey] = useAtom(jobDensityReportKeyAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [imageResponse] = useAtom(imageResponseAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [displayMunicname] = useAtom(displayMunicNameAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [chartImageUrl, setChartImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const chartRef = useRef();

  useEffect(() => {
    // Function to check if map is ready
    const setupMapLoadListener = () => {
      // Try to find map container element
      const mapContainer = chartRef.current?.querySelector('.mapboxgl-map');

      if (mapContainer) {
        const mapInstance = mapContainer._map;
        if (mapInstance) {
          // If map is already loaded
          if (mapInstance.loaded() && mapInstance.areTilesLoaded()) {
            captureChart();
          } else {
            // Listen for map load and style load events
            mapInstance.once('load', () => {
              mapInstance.once('idle', () => {
                captureChart();
              });
            });
          }
        } else {
          // If we can't find map instance, proceed with capture after timeout
          setTimeout(captureChart, 15000);
        }
      } else {
        // If we can't find map container, proceed with capture after timeout
        setTimeout(captureChart, 15000);
      }
    };

    // Capture the chart once map is ready
    const captureChart = () => {
      console.log("Map ready, capturing Job Density chart");

      // Fix for malformed URLs and security issues
      const options = {
        cacheBust: true, // Helps with loading resources properly
        quality: 1.0, // Use highest quality
        // Preserve original dimensions and scaling
        pixelRatio: window.devicePixelRatio,
        // Properly handle loading errors with specific resources
        onCloneNode: (node) => {
          // Fix for mapbox specific elements to prevent malformed URLs
          if (node.tagName === 'LINK' &&
              node.href &&
              node.href.includes('mapbox-gl')) {
            // Clone the node to avoid modifying the original
            const clonedNode = node.cloneNode(true);
            // Set crossorigin attribute to allow proper resource loading
            clonedNode.setAttribute('crossorigin', 'anonymous');
            return clonedNode;
          }

          // Fix for Mapbox control SVGs in stylesheets
          if (node.tagName === 'STYLE') {
            // Skip processing CSS with data URLs to prevent URL mangling
            const styleContent = node.textContent;
            if (styleContent &&
                (styleContent.includes('mapbox') ||
                 styleContent.includes('data:image/svg+xml'))) {
              const clonedNode = node.cloneNode(true);
              // Mark this stylesheet to be preserved as-is
              clonedNode.setAttribute('data-html-to-image-skip', 'true');
              return clonedNode;
            }
          }

          return node;
        },
        // Skip Mapbox control elements that aren't needed for the PDF
        filter: (node) => {
          // Skip mapbox-specific controls (zoom buttons, compass, etc.)
          if (node.classList &&
              (node.classList.contains('mapboxgl-ctrl') ||
               node.classList.contains('mapboxgl-control') ||
               node.classList.contains('mapboxgl-ctrl-group'))) {
            return false;
          }
          return true;
        },
        // Custom fetch for resources to handle CORS issues
        fetchRequestInit: {
          mode: 'cors',
          credentials: 'same-origin'
        }
      };

      toPng(chartRef.current, options)
        .then((dataUrl) => {
          console.log("Job Density chart capture successful");
          setChartImageUrl(dataUrl);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Failed to convert Job Density chart to image:", err);
          // If there's an error, still proceed with PDF generation
          // but log detailed information to help with debugging
          if (err.message && err.message.includes("SecurityError")) {
            console.warn("SecurityError encountered during image conversion. This is likely due to CORS restrictions on external resources.");
          }

          // Create a simple fallback image with a message
          const canvas = document.createElement('canvas');
          canvas.width = 800;
          canvas.height = 600;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#f8f9fa';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.font = '16px Arial';
          ctx.fillStyle = '#212529';
          ctx.textAlign = 'center';
          ctx.fillText('Map visualization temporarily unavailable in PDF', canvas.width/2, canvas.height/2);

          const fallbackImage = canvas.toDataURL('image/png');
          setChartImageUrl(fallbackImage);
          setIsLoading(false);
        });
    };

    // Store timeout IDs for cleanup
    const setupTimeoutId = setTimeout(setupMapLoadListener, 3000);

    // Fallback in case event listeners don't fire or map doesn't load properly
    const fallbackTimeoutId = setTimeout(() => {
      if (!chartImageUrl) {
        console.log("Fallback: Capturing Job Density chart after timeout");
        captureChart();
      }
    }, 25000); // Extended timeout for map loading

    // CRITICAL: Cleanup function to prevent memory leaks
    return () => {
      console.log("JobDensityPdf component unmounting - cleaning up");
      clearTimeout(setupTimeoutId);
      clearTimeout(fallbackTimeoutId);
      
      // Clear image data to free memory
      setChartImageUrl(null);
      setIsLoading(true);
      
      // Clean up any map instances
      const mapElements = document.querySelectorAll('.mapboxgl-map');
      mapElements.forEach(el => {
        if (el && el._map) {
          try {
            el._map.off();
            el._map.remove();
          } catch (err) {
            console.warn("Error cleaning up map instance:", err);
          }
        }
      });
      
      // Force garbage collection hint
      if (window.gc) {
        window.gc();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isLoading ? (
        <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
          <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
            <CircularProgress color="success" size="3rem" />
          </Box>
        </div>
      ) : chartImageUrl ? (
        <PDFViewer style={{ flex: 1, width: "100%", height: "100vh" }}>
          <Document>
            <Page size="A4" style={styles.page}>
              <Image style={styles.headerLogo} src={`data:image/png;base64,'${imageResponse[13].base64_image}'`}></Image>
              <Text style={styles.headerText}>{selectedSpatialLayer?.name} | {displayMunicname}</Text>
              <View>
                <Text style={{ ...styles.header, backgroundColor: '#009640' }}>Industry Diagnostic</Text>
              </View>
              <View style={styles.section}>
                <Text style={styles.heading}>JOB DENSITY</Text>
              </View>
              <View style={{ ...styles.section, paddingBottom: 0 }}>
                <Text style={styles.subheading}>Year: {selectedFilters?.singleYear}</Text>
              </View>
              <View style={{ ...styles.section, height: 600, position: 'absolute', right: 5, top: 90 }}>
                <Image style={styles.image} src={chartImageUrl} />
              </View>
              <View style={{ marginTop: 530 }}>
                <Text style={styles.paragraph}>
                  Where are jobs concentrated within {displayMunicname}?
                </Text>
                <Text style={styles.paragraph}>
                  The density or concentration of jobs is indicative of the form and function of cities. Cities sometimes include a number of sub-centres of economic activity with distinct specialisations. These hubs depend on sufficient investment in infrastructure to densify without becoming overloaded and congested. Spatial patterns can be explored in greater detail using the ‘map explorer’ tool.
                </Text>
              </View>
              <View style={styles.footerContainer}>
                <Text style={styles.footer}>
                  www.spatialtaxdata.org.za
                </Text>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      ) : null}
      <div style={{ position: "absolute", left: "-10000px" }}>
        <div ref={chartRef} >
          <div style={{ height: 1000, aspectRatio: 1 }}>
            <DashboardMap
              key={reportKey}
              reportKey={reportKey}
              data={mapData}
              data_grey={mapDataGrey}
              metro={selectedPlace?.name}
              output={"fte"}
              viewAs={"Absolute"}
              maxValue={parseFloat(maxValue)}
              minValue={parseFloat(minValue)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default JobDensityPdf;
